/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 홍보관기준 통계테이블
 * 버전 : 1.0
 * 파일명 : Promoter_AnalysisTable.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2026-01-30, 강동형 : name값 제거
 * ================================================================*/

import { useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PromoterAnalysiseData } from './useLoadPromoterAnalysis';

const TableHeaderSx = {
  backgroundColor: '#30515B',
  color: 'white',
  fontSize: '14px',
  textAlign: 'center',
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
  },
  '&:first-of-type': {
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
  },
  '&:last-of-type': {
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  },
}

const TableRowSx = {
  height: '49px',
  '& > td:first-of-type': {
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
  },
  '& > td:last-of-type': {
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  },
  '& > td': {
    background: '#FFF',
    boxShadow: '1px 2px 2px rgba(0, 0, 0, 0.1)',
    padding: '4px',
  },
}

const TableCellSx = {
  textAlign: 'center',
  verticalAlign: 'middle',
  fontSize: '16px',
}

export const getStatusFromRow = (row: any): string => {
  const { is_active, admin_approval } = row;
  if (!is_active && !admin_approval) return '대기';
  if (!is_active && admin_approval) return '차단';
  if (is_active && admin_approval) return '승인';
  return ''; // 기타 상태는 필터링 제외
};

interface Props{
  propsData : PromoterAnalysiseData[]
}

const Promoter_AnalysisTable = ({propsData}:Props) => {

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 8,
  });
  const totalPages = Math.ceil(propsData.length / pagination.pageSize);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: page }));
  };
  
  return (
    <div style={{height:'100%'}}>
    <MaterialReactTable
      data={propsData}
      muiTableContainerProps={{
        sx: { 
          height: 'calc(100vh - 450px)',
          overflowY: 'auto',
          overflowX: 'auto',
          width: 'calc(100vw - 425px)'
        }
      }}
      columns={[
        {
          header: '순서',
          Cell: ({ row }) => row.index + 1, // 0부터 시작하므로 +1
          size: 50, // 필요에 따라 너비 지정
          enableSorting: false, // ✅ 정렬 비활성화
        },
        { header: '이름(아이디)',
          accessorFn: (row) => `${row.user_name} (${row.user_id})`,
        },
        { accessorKey: 'participation_count', header: '홍보횟수' },
        { accessorKey: 'sum_total_cnt', header: '참여자 수' },
        {
          header: '관심표명자 수(%)',
          accessorFn: (row) => ({
            count: row.sum_interested_cnt,
            percent:
              row.participation_count && row.participation_count !== 0
                ? (row.sum_interested_cnt / row.participation_count) * 100
                : 0,
          }),
          Cell: ({ cell }) => {
            const { count, percent } = cell.getValue() as { count: number; percent: number };
            return `${count} (${percent.toFixed(1)}%)`;
          },
        },
        {
          header: '지원자 수(%)',
          accessorFn: (row) => ({
            count: row.sum_apply_cnt,
            percent:
              row.sum_interested_cnt && row.sum_interested_cnt !== 0
                ? (row.sum_apply_cnt / row.sum_interested_cnt) * 100
                : 0,
          }),
          Cell: ({ cell }) => {
            const { count, percent } = cell.getValue() as { count: number; percent: number };
            return `${count} (${percent.toFixed(1)}%)`;
          },
        },
        {
          header: '임관 수(%)',
          accessorFn: (row) => ({
            count: row.commissioned_count,
            percent:
              row.sum_apply_cnt && row.sum_apply_cnt !== 0
                ? (row.commissioned_count / row.sum_apply_cnt) * 100
                : 0,
          }),
          Cell: ({ cell }) => {
            const { count, percent } = cell.getValue() as { count: number; percent: number };
            return `${count} (${percent.toFixed(1)}%)`;
          },
        },
        {
          header: '관심상실자 수(%)',
          accessorFn: (row) => ({
            count: row.not_interested_count,
            percent:
              row.sum_interested_cnt && row.sum_interested_cnt !== 0
                ? (row.not_interested_count / row.sum_interested_cnt) * 100
                : 0,
          }),
          Cell: ({ cell }) => {
            const { count, percent } = cell.getValue() as { count: number; percent: number };
            return `${count} (${percent.toFixed(1)}%)`;
          },
        },
      ]}
      // 툴바, sort 버튼 등
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enableColumnActions={false}
      enableSorting={true}
      muiTablePaperProps={{
        elevation: 0, // ✅ 그림자 제거
      }}
      // 로우 헤더 스타일
      muiTableHeadCellProps={{
        sx: TableHeaderSx,
      }}
      // 로우 스타일
      muiTableBodyRowProps={{
      sx: TableRowSx,
      }}
      // 셀 스타일
      muiTableBodyCellProps={{
        sx: TableCellSx,
      }}
      // 테이블 스타일
      muiTableProps={{
        sx: {
          borderCollapse: 'separate',
          borderSpacing: '0 5px', // ✅ row 간격 5px
          '& .MuiTableSortLabel-icon': {
            color: '#aaa',
            fontSize: '1.5rem',
          },
          '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
            color: 'rgb(255, 255, 255) !important',
          },
      }}}
      // 페이지 네이션 옵션
      state={{ pagination }}
      onPaginationChange={setPagination}
      pageCount={totalPages} // 수동 계산 필요 시
      />
        <Box display="flex" justifyContent="center" mt={2} gap={1}>
          <IconButton
            onClick={() => handlePageChange(Math.max(pagination.pageIndex - 1, 0))}
            disabled={pagination.pageIndex === 0}
            aria-label="이전 페이지"
          >
            <ChevronLeft />
          </IconButton>

          {Array.from({ length: totalPages }).map((_, index) => (
            <IconButton
              key={index}
              onClick={() => handlePageChange(index)}
              sx={{
                backgroundColor:
                  pagination.pageIndex === index ? '#50818b' : 'transparent',
                color: pagination.pageIndex === index ? '#fff' : '#666',
                '&:hover': {
                  backgroundColor:
                    pagination.pageIndex === index ? '#4a747d' : '#f0f0f0',
                },
                width: 32,
                height: 32,
                fontSize: 14,
              }}
            >
              {index + 1}
            </IconButton>
          ))}

          <IconButton
            onClick={() =>
              handlePageChange(Math.min(pagination.pageIndex + 1, totalPages - 1))
            }
            disabled={pagination.pageIndex === totalPages - 1}
            aria-label="다음 페이지"
          >
            <ChevronRight />
          </IconButton>
      </Box>
    </div>
  );
};

export default Promoter_AnalysisTable;
